import React from "react";
import { Plus, X } from "lucide-react";

// Compact tab bar rendered directly above the canvas. Each tab represents
// one in-memory activity; the active tab is the one whose state the shared
// canvas is currently rendering. Each tab carries a delete control that
// disappears when only a single tab remains so the workspace can never be
// left empty.
export const ActivityTabs = ({
  tabs, // [{ tabId, name }]
  activeTabId,
  onSelectTab,
  onCreateTab,
  onDeleteTab,
}) => {
  const canDelete = tabs.length > 1;
  return (
    <div
      className="flex items-center gap-1 border-b border-slate-200 bg-white px-6 py-1.5 overflow-x-auto"
      data-testid="activity-tabs"
      role="tablist"
      aria-label="Activities"
    >
      {tabs.map((t, i) => {
        const active = t.tabId === activeTabId;
        const label = (t.name && t.name.trim()) || `Activity ${i + 1}`;
        return (
          <div
            key={t.tabId}
            className={
              "group shrink-0 flex items-center rounded-md transition-colors " +
              (active
                ? "bg-slate-900 text-white"
                : "bg-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-100")
            }
            data-testid={`activity-tab-wrap-${t.tabId}`}
          >
            <button
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelectTab(t.tabId)}
              data-testid={`activity-tab-${t.tabId}`}
              title={label}
              className="max-w-[180px] truncate pl-3 pr-1 h-8 text-xs font-semibold"
            >
              <span className="block truncate">{label}</span>
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteTab(t.tabId);
                }}
                data-testid={`activity-tab-close-${t.tabId}`}
                aria-label={`Delete ${label}`}
                title="Delete activity"
                className={
                  "w-6 h-6 mr-1 flex items-center justify-center rounded transition-colors " +
                  (active
                    ? "text-slate-300 hover:text-white hover:bg-white/10"
                    : "text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100")
                }
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={onCreateTab}
        data-testid="activity-tab-add-btn"
        aria-label="New activity"
        title="New activity"
        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors ml-1"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
};
